const OPENS_AFTER = /[\s([{"'“‘]/;
// A fence opener: 3+ backticks (whose info string has none) or 3+ tildes, indented 0-3 spaces.
const OPENER = /^ {0,3}(`{3,}(?=[^`]*$)|~{3,})/;
const INDENTED = /^( {4}|\t)/;

/** Emphasis left open in `prose` (one paragraph, code already blanked), innermost first. */
function openEmphasis(prose: string): string[] {
  const stack: string[] = [];
  for (const match of prose.matchAll(/\*\*|\*|_/g)) {
    const at = match.index ?? 0;
    const before = at === 0 ? " " : prose[at - 1];
    const after = prose[at + match[0].length] ?? " ";
    if (/\S/.test(before) && stack.at(-1) === match[0]) stack.pop();
    else if (/\S/.test(after) && OPENS_AFTER.test(before)) stack.push(match[0]);
  }
  return stack.reverse();
}

/** Closes what `paragraph` (prose, no code blocks) left open: inline code, then emphasis. */
function closeParagraph(paragraph: string, atEnd: boolean): string {
  let body = paragraph.replace(/\s+$/, "");
  // A marker with nothing after it yet (the stream stopped right after it) is dropped.
  const dangling = atEnd ? body.match(/(^|\s)(\*\*|\*|_|`)$/) : null;
  if (dangling) body = body.slice(0, body.length - dangling[2].length).replace(/\s+$/, "");
  const openCode = (body.match(/`/g)?.length ?? 0) % 2 === 1;
  // Inline code is blanked for the emphasis scan, an unclosed span up to the end too.
  const prose = body
    .replace(/`[^`]*`/g, (code) => " ".repeat(code.length))
    .replace(/`[^`]*$/, (code) => " ".repeat(code.length));
  return body + (openCode ? "`" : "") + openEmphasis(prose).join("");
}

/**
 * An unfinished answer (streaming, or stopped mid-way) with the Markdown it left open at its end
 * closed, so a half-written "*Hi Avery" reads as italic text, not a literal asterisk.
 *
 * Code is never touched: fenced blocks, indented (4-space) code and inline code spans are not
 * scanned for emphasis, and nothing is ever appended onto a fence line. An unclosed fence gets
 * its closer on a line of its own, after the emphasis before the fence is closed. Only the last
 * prose paragraph (before any open block) is repaired; finished text needs none of this.
 */
export function closeOpenMarkdown(text: string): string {
  const lines = text.split("\n");
  // The fence still open at the end: its line, and its run (the closer must match it).
  let open: { line: number; run: string } | null = null;
  const code: boolean[] = [];
  lines.forEach((line, index) => {
    if (open) {
      // CommonMark: only a line of the opener's character, at least as long, closes it.
      const closer = line.match(/^ {0,3}(`{3,}|~{3,})\s*$/)?.[1];
      if (closer && closer[0] === open.run[0] && closer.length >= open.run.length) open = null;
      code.push(true);
      return;
    }
    const opener = line.match(OPENER)?.[1];
    if (opener) {
      open = { line: index, run: opener };
      code.push(true);
      return;
    }
    // Indented code starts after a blank line (or the start) and runs while lines stay indented.
    const previous = index - 1;
    const afterBreak = previous < 0 || lines[previous].trim() === "";
    // Any code line before it counts: a closed fence too (indented code may follow a fence).
    const continues = previous >= 0 && code[previous];
    code.push(INDENTED.test(line) && line.trim() !== "" && (afterBreak || continues));
  });
  const fence = open as { line: number; run: string } | null;

  let end = fence ? fence.line : lines.length;
  // Blank lines before the open fence, or at the very end (a stream often pauses on a line
  // break), are not a boundary: the prose above them is what is left open.
  while (end > 0 && lines[end - 1].trim() === "") end -= 1;
  // The last prose paragraph before `end`: non-blank, non-code lines back to a boundary.
  let start = end;
  while (start > 0 && !code[start - 1] && lines[start - 1].trim() !== "") start -= 1;

  const closer = fence ? `\n${fence.run[0].repeat(fence.run.length)}` : "";
  // Nothing in prose to repair (the text ends in code).
  if (start === end) return text + closer;

  const head = lines.slice(0, start).join("\n");
  const paragraph = closeParagraph(lines.slice(start, end).join("\n"), !fence);
  const joined = start > 0 ? `${head}\n${paragraph}` : paragraph;
  if (!fence) return joined;
  return `${joined}\n${lines.slice(end).join("\n")}${closer}`;
}
